import {marked} from 'marked'
import {markedEmoji} from "marked-emoji"
import {mdEmojiOptions} from "../lib/md-emojis"

marked.use(markedEmoji(mdEmojiOptions))

export default {
  props: {
    channelId: {type: String, required: true},
  },

  data() {
    return {
      postsPerPage: 35,
      reverseOrder: true,
      pinnedOnly: false,
      orderedPostIds: [],
      posts: {},
      postImages: {},
      postFiles: {},
      postReactions: {},
      currentPage: 0,
      prevPostId: '',
      users: {},
      avatars: {},
      postInput: '',
      postInputRootId: '',
      postEditId: '',
      pendingFile: null,
      connected: false,
      minRows: 1,
      maxRows: 4,
      emojis: mdEmojiOptions.emojis,
      loggedIn: false,
      error: '',
      subcribeTries: 0,
      extraRows: 0,
      viewerImages: null,
      viewerIndex: 0,
    }
  },

  computed: {
    currentRows() {
      // Calculate the number of rows based on the textarea content
      const lines = this.postInput.split('\n').length + this.extraRows;
      this.extraRows = 0
      return Math.min(this.maxRows, Math.max(this.minRows, lines));
    },
    isOffline() {
      return !navigator.onLine
    },
  },

  created() {
    if (this.$storage.getToken() && navigator.onLine) {
        this.fetchChannelPosts()
        this.subscribeWebSocket()
        this.markChannelAsRead()
    }
  },

  beforeDestroy() {
    this.unSubscribeWebSocket()
  },

  methods: {
    markChannelAsRead() {
      this.$storage.markChannelAsRead(this.channelId)
    },

    openImageViewer(images, index) {
      this.viewerImages = images
      this.viewerIndex = index || 0
    },

    closeImageViewer() {
      this.viewerImages = null
    },

    onPostClick(postId, event) {
      if (event.target.tagName.toLowerCase() === 'a') {
        return; // Link wurde angeklickt, Menü nicht anzeigen
      }
      this.postInputRootId = ''
      this.postEditId = ''
      let value = !this.posts[postId].showMenu
      this.$set(this.posts[postId], 'showMenu', value)
      if (value) {
        for (const [pid, post] of Object.entries(this.posts)) {
          if (pid == postId) continue
          if (!post.showMenu) continue
          this.$set(this.posts[pid], 'showMenu', false)
        }
      }
    },

    convert2Html(markdown) {
      return marked(markdown)
    },

    getFormatedDate(timestamp) {
      // accepts ISO datetime string (from Django) or Unix ms timestamp
      let date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(parseInt(timestamp))
      return date.toISOString().slice(0,10) + ' ' + date.toTimeString().slice(0,5)
    },

    getTimeOnly(timestamp) {
      let date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(parseInt(timestamp))
      return date.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'})
    },

    getDayLabel(timestamp) {
      let date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(parseInt(timestamp))
      let today = new Date()
      today.setHours(0,0,0,0)
      let postDay = new Date(date)
      postDay.setHours(0,0,0,0)
      let diff = today - postDay
      if (diff === 0) return 'Heute'
      if (diff === 86400000) return 'Gestern'
      return date.toLocaleDateString('de-DE', {day: 'numeric', month: 'long', year: 'numeric'})
    },

    scrollDown() {
      let content = document.querySelector('.c-chat-content');
      if (content) {
        content.scrollTop = content.scrollHeight;
      } else {
        try {
          window.scrollTo(0, document.body.scrollHeight);
        } catch (error) { console.error(error) }
      }
    },

    userFullName(postId) {
      let post = this.posts[postId]
      // User info comes inline with the post from our chat API
      return `${post.first_name || ''} ${post.last_name || ''}`.trim() || post.username
    },

    fetchAndSetAvatars(posts) {
      let me = this
      let userIds = [...new Set(Object.values(posts).map(p => p.user_id))]
      let params = {limit: userIds.length, uuid: userIds}
      me.$api.get('/community-users/', {params: params}).then(response => {
        for (const user of response.data.results) {
          me.$set(me.avatars, user.uuid, user.profile_image)
        }
      })
    },

    buildPostReactions(posts) {
      // Build postReactions from inline reactions data
      for (const post of Object.values(posts)) {
        if (post.reactions && post.reactions.length) {
          for (const reaction of post.reactions) {
            this.addPostReaction(post.id, reaction.emoji_name, reaction.user_id)
          }
        }
      }
    },

    buildPostAttachments(posts) {
      // Build postImages and postFiles from inline attachments data
      let me = this
      for (const post of Object.values(posts)) {
        if (post.attachments && post.attachments.length) {
          let images = []
          let files = []
          for (const att of post.attachments) {
            if (att.mime_type && att.mime_type.startsWith('image')) {
              images.push({src: att.file, name: att.filename})
            } else if (att.mime_type !== 'text/calendar') {
              files.push({src: att.file, name: att.filename, size: att.file_size})
            }
          }
          if (images.length) me.$set(me.postImages, post.id, images)
          if (files.length) me.$set(me.postFiles, post.id, files)
        }
      }
    },

    addPostReaction(postId, emojiName, userId) {
      if (postId in this.postReactions) {
        if (emojiName in this.postReactions[postId]) {
          let users = new Set([...this.postReactions[postId][emojiName], userId])
          this.$set(this.postReactions[postId], emojiName, users)
        } else {
          this.$set(this.postReactions[postId], emojiName, new Set([userId]))
        }
      } else {
        let info = {}
        info[emojiName] = new Set([userId])
        this.$set(this.postReactions, postId, info)
      }
    },

    removePostReaction(postId, emojiName, userId) {
      let set = this.postReactions[postId][emojiName]
      set.delete(userId)
      this.$set(this.postReactions[postId], emojiName, new Set([...set]))
    },

    _insertPost(post) {
      let isNew = !this.orderedPostIds.includes(post.id)
      if (isNew) {
        this.orderedPostIds.push(post.id)
      }
      this.$set(this.posts, post.id, post)
      this.buildPostAttachments({[post.id]: post})
      if (isNew) {
        if (post.user_id && !this.avatars[post.user_id]) {
          this.fetchAndSetAvatars({[post.id]: post})
        }
        if (this.reverseOrder) setTimeout(() => { this.scrollDown() }, 300)
      }
    },

    updatePost(post) {
      this.$set(this.posts[post.id], 'message', post.message)
    },

    removePost(postId) {
      delete this.postImages[postId]
      delete this.postFiles[postId]
      delete this.postReactions[postId]
      delete this.posts[postId]
      let idx = this.orderedPostIds.indexOf(postId)
      this.orderedPostIds.splice(idx, 1)
    },

    fetchOlderPosts() {
      this.currentPage++
      this.fetchChannelPosts(false)
    },

    subscribeChannel() {
      let me = this
      me.subcribeTries += 1
      me.$chat.post(`channels/${this.channelId}/members/`).then(() => {
        if (me.subcribeTries < 2) me.fetchChannelPosts()
      })
    },

    fetchChannelPosts(scrollDown=true) {
      let me = this
      let params = {
        page: this.currentPage,
        per_page: this.postsPerPage,
      }
      let url = this.pinnedOnly
        ? `channels/${this.channelId}/pinned/`
        : `channels/${this.channelId}/posts/`
      this.$chat.get(url, {params: params}).then(response => {
          me.error = ''
          me.orderedPostIds.unshift(...response.data.order.reverse())
          me.posts = { ...me.posts, ...response.data.posts }
          me.prevPostId = response.data.prev_post_id

          if (me.orderedPostIds.length) {
            // User info, reactions, and attachments come inline - no extra requests needed
            me.fetchAndSetAvatars(response.data.posts)
            me.buildPostReactions(response.data.posts)
            me.buildPostAttachments(response.data.posts)
            if (scrollDown && me.reverseOrder)
              setTimeout(function() {me.scrollDown()}, 500);
          }
      }, error_response => {
        console.error(error_response)
        if (error_response.response && error_response.response.status == 403)
          me.subscribeChannel()
        me.error = 'cannot fetch posts, check channel access and ID!'
      })
    },

    postMessage() {
      console.log('channel-mixin.postMessage()')
      if (this.postEditId) {
        this.putPost(this.postEditId)
      } else {
        this.createPost()
      }
    },

    createPost() {
        // Prevent empty posts (unless there's a file)
        if (!this.postInput || !this.postInput.trim()) {
          if (!this.pendingFile) return Promise.resolve(null)
        }

        let me = this

        // If there's a file, use FormData with multipart upload
        if (this.pendingFile) {
          let formData = new FormData()
          formData.append('channel', this.channelId)
          formData.append('message', this.postInput || '')
          if (this.postInputRootId)
            formData.append('root_id', this.postInputRootId)
          formData.append('file', this.pendingFile)

          return this.$chat.post('posts/', formData, {
            headers: {'Content-Type': 'multipart/form-data'}
          }).then((response) => {
              me._insertPost(response.data)
              me.postInput = ''
              me.postInputRootId = ''
              me.pendingFile = null
              me.joinChannelWebSocket(me.channelId)
              return response
          })
        }

        // Text-only post (existing logic)
        let payload = {
          channel: this.channelId,
          message: this.postInput,
        }
        if (this.postInputRootId)
          payload.root_id = this.postInputRootId

        return this.$chat.post('posts/', payload).then((response) => {
            me._insertPost(response.data)
            me.postInput = ''
            me.postInputRootId = ''
            me.joinChannelWebSocket(me.channelId)
            return response
        })
    },

    putPost(postId) {
        // Prevent empty posts
        if (!this.postInput || !this.postInput.trim()) {
          return Promise.resolve(null)
        }

        let post = this.posts[postId]
        let payload = {
          channel: post.channel_id,
          message: this.postInput,
        }
        let me = this
        return this.$chat.put(`posts/${postId}/`, payload).then((response) => {
            me._insertPost(response.data)
            me.postInput = ''
            me.postEditId = ''
            me.postInputRootId = ''
            return response
        })
    },

    editPost(postId) {
      this.posts[postId].showMenu = false
      this.postEditId = postId
      this.postInput = this.posts[postId].message
      this.$refs.postInput.focus()
    },

    deletePost(postId) {
        this.$chat.delete(`posts/${postId}/`)
    },

    fetchOrCreateDirectChannel(otherUserUuid) {
      return this.$chat.post('channels/direct/', {
        user_uuid: otherUserUuid,
      }).then(response => response.data)
    },

    formatReply(username, oldMessage) {
      function getLine(str, num=0) {
        let lines = str.split('\n')
        if (lines.length > num) {
          return lines[num].substring(0, 119).trim()
        }
        return ''
      }
      function stripReplyLines(str) {
        let lines = str.split('\n');

        // Filter out lines that start with ">"
        let filteredLines = lines.filter(line => !line.trim().startsWith('>'))
        return filteredLines.join('\n').trim()
      }

      let msg = stripReplyLines(oldMessage)
      msg = `> ${username}: ${getLine(msg)}`
      let secondLn = getLine(oldMessage, 1)
      if (secondLn) {
        msg += ` ${secondLn}`
        this.extraRows = 1
      }
      let thirdLn = getLine(oldMessage, 2)
      if (thirdLn) {
        msg += ` ${thirdLn}`
        this.extraRows = 2
      }
      msg += '\n\n'
      return msg
    },

    replyPrivateToPost(postId) {
      let post = this.posts[postId]
      let otherUserUuid = post.user_id
      this.fetchOrCreateDirectChannel(otherUserUuid).then(channel => {
        let url = `/community/${otherUserUuid}/${channel.id}?reply_to_post_id=${postId}`
        this.$router.push(url)
      })
    },

    replyToPost(postId) {
      this.posts[postId].showMenu = false
      this.postInputRootId = this.posts[postId].root_id || postId
      let username = this.posts[postId].username
      this.postInput = this.formatReply(username, this.posts[postId].message)
      this.$refs.postInput.focus()
    },

    reactToPost(postId, emojiName) {
        this.posts[postId].showMenu = false
        let userUuid = this.$storage.getUserUuid()
        if (postId in this.postReactions && emojiName in this.postReactions[postId] &&
            this.postReactions[postId][emojiName].has(userUuid)) {
          // delete reaction
          this.$chat.delete(`reactions/${postId}/${emojiName}/`)
        } else {
          // add reaction
          let payload = {
            'post_id': postId,
            'emoji_name': emojiName,
          }
          this.$chat.post('reactions/', payload)
        }
    },

    leaveChannel() {
      let me = this
      return this.$chat.delete(`channels/${this.channelId}/members/`).then(() => {
        if (me.$storage.websocket && me.$storage.websocket.readyState === WebSocket.OPEN) {
          me.$storage.websocket.send(JSON.stringify({
            action: 'leave_channel',
            channel_id: me.channelId,
          }))
        }
        me.$router.back()
      })
    },

    joinChannelWebSocket(channelId) {
      if (this.$storage.websocket && this.$storage.websocket.readyState === WebSocket.OPEN) {
        this.$storage.websocket.send(JSON.stringify({
          action: 'join_channel',
          channel_id: channelId,
        }))
      }
    },

    subscribeWebSocket() {
      if (this.$storage.websocket) {
        console.log('subscribe chat websocket.')
        this.$storage.websocket.addEventListener('message', this.onWebsocketMessage)
      }
    },

    unSubscribeWebSocket() {
      if (this.$storage.websocket) {
        console.log('unsubscribe chat websocket.')
        this.$storage.websocket.removeEventListener('message', this.onWebsocketMessage)
      }
    },

    onWebsocketMessage(event) {
      let me = this
      const message = JSON.parse(event.data);
      if (message.event === 'posted') {
        let post = message.data
        if (post.channel_id === me.channelId) {
          console.log('New post received:', post);
          me._insertPost(post)
        }
      } else if (message.event === 'reaction_added') {
        let data = message.data
        me.addPostReaction(data.post_id, data.emoji_name, data.user_id)
      } else if (message.event === 'reaction_removed') {
        let data = message.data
        me.removePostReaction(data.post_id, data.emoji_name, data.user_id)
      } else if (message.event === 'post_edited') {
        let post = message.data
        me.posts[post.id] = post
        if (me.pinnedOnly && post.is_pinned)
          me.orderedPostIds.unshift(post.id)
        me.updatePost(post)
      } else if (message.event === 'post_deleted') {
        let data = message.data
        me.removePost(data.id)
      } else if (message.event === 'meeting_updated') {
        let data = message.data
        if (data.post_id in me.posts) {
          me.$set(me.posts[data.post_id], 'meeting_status', data.meeting_status)
        }
      }
    }
  }
}
